import { useState, type ReactNode } from 'react';
import { BRAND } from '@fin/shared/brand';

export function Button({
  children,
  variant = 'primary',
  ...rest
}: { variant?: 'primary' | 'secondary' | 'danger' | 'white' } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const styles =
    variant === 'primary'
      ? { backgroundColor: BRAND.primary, color: 'white' }
      : variant === 'danger'
        ? { backgroundColor: BRAND.danger, color: 'white' }
        : variant === 'white'
          ? { backgroundColor: 'white', color: BRAND.primary, border: 'none' }
          : { backgroundColor: 'white', color: BRAND.primary, border: `1px solid ${BRAND.border}` };
  return (
    <button
      {...rest}
      style={{ ...styles, ...rest.style }}
      className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium shadow-sm transition hover:shadow-md disabled:opacity-50 ${rest.className ?? ''}`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-stone-200 bg-white shadow-sm ${className}`}>{children}</div>
  );
}

export function PageHeader({
  title,
  subtitle,
  icon,
  accent,
  action,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Optional gradient pair [from, to]. Defaults to brand primary → primarySoft. */
  accent?: { from: string; to: string };
  action?: ReactNode;
}) {
  const from = accent?.from ?? BRAND.primary;
  const to = accent?.to ?? (BRAND.primarySoft ?? BRAND.primary);
  return (
    <div
      className="relative mb-5 overflow-hidden rounded-2xl px-5 py-4 shadow-sm sm:mb-6 sm:px-6 sm:py-5"
      style={{ backgroundImage: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-15 blur-2xl"
        style={{ background: 'white' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full opacity-10 blur-2xl"
        style={{ background: 'white' }}
      />
      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {icon && (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm sm:h-11 sm:w-11"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold text-white drop-shadow-sm sm:text-2xl">{title}</h1>
            {subtitle && <p className="truncate text-xs text-white/80 sm:text-sm">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="flex max-h-[95vh] w-full max-w-lg flex-col rounded-t-xl bg-white shadow-xl sm:rounded-xl"
        style={{ borderTopColor: BRAND.primary, borderTopWidth: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-stone-200 px-4 py-3 sm:px-5">
          <h2 className="font-semibold" style={{ color: BRAND.primary }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-700" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

export function useDisclosure() {
  const [open, setOpen] = useState(false);
  return { open, onOpen: () => setOpen(true), onClose: () => setOpen(false) };
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-stone-700">{label}</span>
      {children}
      {hint ? <span className="text-xs text-stone-500">{hint}</span> : null}
    </label>
  );
}

export const inputClass =
  'w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2';
